import { useState, useEffect } from "react";
import api from "@/lib/api";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  ExternalLink, 
  Mail, 
  Linkedin, 
  FileText, 
  HelpCircle,
  Sparkles,
  RefreshCw,
  Target,
  MapPin,
  DollarSign,
  Shield,
  Copy,
  Check,
  Trash2,
  Zap
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "call_set", label: "Call Set" },
  { value: "submitted", label: "Submitted" },
  { value: "interview", label: "Interview" },
  { value: "offer", label: "Offer" },
  { value: "rejected", label: "Rejected" },
  { value: "archived", label: "Archived" },
];

export default function LeadDetailSheet({ lead, open, onClose, onUpdate }) {
  const [enriching, setEnriching] = useState(false);
  const [generatingDrafts, setGeneratingDrafts] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [currentLead, setCurrentLead] = useState(lead);

  useEffect(() => {
    setCurrentLead(lead);
  }, [lead]);

  const getScoreColor = (score) => {
    if (score >= 80) return "text-emerald-400";
    if (score >= 50) return "text-theme-secondary";
    return "text-theme-danger";
  };

  const handleStatusChange = async (status) => {
    try {
      await api.put(`/leads/${lead.id}/status?status=${status}`);
      toast.success(`Status updated to ${status.replace('_', ' ')}`);
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to update status");
      console.error(error);
    }
  };

  const handleEnrich = async () => {
    try {
      setEnriching(true);
      const response = await api.post(`/leads/${lead.id}/enrich`);
      setCurrentLead(response.data);
      toast.success("Lead enriched successfully");
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to enrich lead");
      console.error(error);
    } finally {
      setEnriching(false);
    }
  };

  const handleGenerateDrafts = async () => {
    try {
      setGeneratingDrafts(true);
      const response = await api.post(`/leads/${lead.id}/generate-drafts`);
      setCurrentLead(response.data);
      toast.success("Drafts generated successfully");
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to generate drafts");
      console.error(error);
    } finally {
      setGeneratingDrafts(false);
    }
  };

  const handleRescore = async () => {
    try {
      setScoring(true);
      const response = await api.post(`/leads/${lead.id}/score`);
      setCurrentLead(response.data);
      toast.success("Lead re-scored successfully");
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to re-score lead");
      console.error(error);
    } finally {
      setScoring(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead deleted");
      onClose();
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to delete lead");
      console.error(error);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (!lead) return null;

  const displayLead = currentLead || lead;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent 
        className="w-[600px] sm:max-w-[600px] p-0 border-l-4"
        style={{
          background: 'linear-gradient(180deg, #1a3a42 0%, #264653 100%)',
          borderColor: '#e9c46a'
        }}
        aria-describedby="lead-detail-description"
      >
        <ScrollArea className="h-full">
          <div className="p-6">
            <SheetHeader className="mb-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <SheetTitle className="text-xl text-theme-light mb-2" style={{textShadow: '1px 1px 2px rgba(0,0,0,0.3)'}}>
                    {displayLead.title}
                  </SheetTitle>
                  <p className="text-theme-muted" id="lead-detail-description">{displayLead.client}</p>
                </div>
                <div className="text-right p-3 rounded-xl bg-theme-dark border-2 border-theme-primary/40" style={{boxShadow: '3px 3px 0 rgba(38, 70, 83, 0.4)'}}>
                  <div className={`text-4xl font-bold font-mono ${getScoreColor(displayLead.score)}`} style={{textShadow: '0 0 15px currentColor'}}>
                    {displayLead.score}
                  </div>
                  <p className="text-xs text-theme-muted font-mono">SCORE</p>
                </div>
              </div>
              
              {/* Quick Info */}
              <div className="flex flex-wrap gap-2 mt-4">
                <Badge variant="outline" className={`status-${displayLead.status} font-mono`}>
                  {displayLead.status.replace('_', ' ').toUpperCase()}
                </Badge>
                <Badge variant="outline" className={`source-${displayLead.source} font-mono`}>
                  {displayLead.source.replace('_', ' ').toUpperCase()}
                </Badge>
                {displayLead.clearance && (
                  <Badge variant="outline" className="border-theme-accent/40 text-theme-accent bg-theme-accent/10 font-mono">
                    <Shield className="w-3 h-3 mr-1" />
                    {displayLead.clearance}
                  </Badge>
                )}
              </div>
            </SheetHeader>

            {/* Status Selector */}
            <div className="mb-6">
              <label className="text-xs text-theme-secondary uppercase tracking-wider mb-2 block font-mono">
                PIPELINE STATUS
              </label>
              <Select value={displayLead.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="bg-theme-dark border-2 border-theme-primary/40 text-theme-light" data-testid="lead-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-theme-dark border-2 border-theme-primary/40">
                  {STATUSES.map(status => (
                    <SelectItem key={status.value} value={status.value} className="text-theme-light">
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 rounded-lg bg-theme-dark/50 border-2 border-theme-primary/30">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-theme-secondary" />
                  <span className="text-xs text-theme-muted uppercase font-mono">RATE</span>
                </div>
                <p className="font-mono text-theme-light font-semibold">{displayLead.rate || "Not specified"}</p>
              </div>
              <div className="p-4 rounded-lg bg-theme-dark/50 border-2 border-theme-primary/30">
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-4 h-4 text-theme-accent" />
                  <span className="text-xs text-theme-muted uppercase font-mono">LOCATION</span>
                </div>
                <p className="text-theme-light text-sm">{displayLead.location}</p>
              </div>
            </div>

            {/* Tech Stack */}
            {displayLead.tech_stack?.length > 0 && (
              <div className="mb-6">
                <label className="text-xs text-theme-secondary uppercase tracking-wider mb-2 block font-mono">
                  TECH STACK
                </label>
                <div className="flex flex-wrap gap-2">
                  {displayLead.tech_stack.map((tech) => (
                    <span 
                      key={tech}
                      className="px-3 py-1.5 text-xs bg-theme-dark text-theme-primary-light rounded-lg font-mono border-2 border-theme-primary/30"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Score Breakdown */}
            {displayLead.score_breakdown && (
              <div className="mb-6 p-4 rounded-xl bg-theme-dark/50 border-2 border-theme-primary/30">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-4 h-4 text-theme-secondary" />
                  <span className="text-sm font-mono text-theme-secondary">SCORE BREAKDOWN</span>
                </div>
                <div className="space-y-3">
                  {Object.entries(displayLead.score_breakdown).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-xs text-theme-muted capitalize font-mono">
                        {key.replace('_', ' ')}
                      </span>
                      <div className="flex items-center gap-3">
                        <div className="w-20 h-2 rounded-full bg-theme-darker overflow-hidden">
                          <div 
                            className="h-full rounded-full bg-gradient-to-r from-[#2a9d8f] to-[#40c9ba]"
                            style={{width: `${value * 4}%`}}
                          />
                        </div>
                        <span className="font-mono text-xs text-theme-light w-6 text-right font-bold">
                          {value}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 mb-6">
              <Button
                className="flex-1 bg-theme-dark border-2 border-theme-primary/40 text-theme-primary-light hover:bg-theme-primary/20 font-mono text-xs"
                onClick={handleRescore}
                disabled={scoring}
                data-testid="rescore-lead-btn"
              >
                {scoring ? (
                  <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Target className="w-4 h-4 mr-1" />
                )}
                RE-SCORE
              </Button>
              <Button
                className="flex-1 bg-theme-dark border-2 border-theme-secondary/40 text-theme-secondary hover:bg-theme-secondary/20 font-mono text-xs"
                onClick={handleEnrich}
                disabled={enriching}
                data-testid="enrich-lead-btn"
              >
                {enriching ? (
                  <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-1" />
                )}
                ENRICH
              </Button>
              <Button
                className="flex-1 bg-theme-dark border-2 border-theme-accent/40 text-theme-accent hover:bg-theme-accent/20 font-mono text-xs"
                onClick={handleGenerateDrafts}
                disabled={generatingDrafts}
                data-testid="generate-drafts-btn"
              >
                {generatingDrafts ? (
                  <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4 mr-1" />
                )}
                GENERATE
              </Button>
            </div>

            {/* Tabs for Action Pack */}
            <Tabs defaultValue="enrichment" className="mb-6">
              <TabsList className="bg-theme-dark border-2 border-theme-primary/30 w-full p-1">
                <TabsTrigger value="enrichment" className="flex-1 data-[state=active]:bg-theme-primary/30 data-[state=active]:text-theme-light text-theme-muted font-mono text-xs">
                  ENRICHMENT
                </TabsTrigger>
                <TabsTrigger value="email" className="flex-1 data-[state=active]:bg-theme-primary/30 data-[state=active]:text-theme-light text-theme-muted font-mono text-xs">
                  EMAIL
                </TabsTrigger>
                <TabsTrigger value="linkedin" className="flex-1 data-[state=active]:bg-theme-primary/30 data-[state=active]:text-theme-light text-theme-muted font-mono text-xs">
                  LINKEDIN
                </TabsTrigger>
                <TabsTrigger value="resume" className="flex-1 data-[state=active]:bg-theme-primary/30 data-[state=active]:text-theme-light text-theme-muted font-mono text-xs">
                  RESUME
                </TabsTrigger>
              </TabsList>

              <TabsContent value="enrichment" className="mt-4">
                {displayLead.enrichment ? (
                  <div className="space-y-4 p-4 rounded-xl bg-theme-dark/30 border-2 border-theme-primary/20">
                    {displayLead.enrichment.company_info && (
                      <div>
                        <label className="text-xs text-theme-secondary uppercase tracking-wider mb-1 block font-mono">
                          COMPANY INFO
                        </label>
                        <p className="text-sm text-theme-light">{displayLead.enrichment.company_info}</p>
                      </div>
                    )}
                    {displayLead.enrichment.program_details && (
                      <div>
                        <label className="text-xs text-theme-secondary uppercase tracking-wider mb-1 block font-mono">
                          PROGRAM DETAILS
                        </label>
                        <p className="text-sm text-theme-light">{displayLead.enrichment.program_details}</p>
                      </div>
                    )}
                    {displayLead.enrichment.timeline && (
                      <div>
                        <label className="text-xs text-theme-secondary uppercase tracking-wider mb-1 block font-mono">
                          TIMELINE
                        </label>
                        <p className="text-sm text-theme-light">{displayLead.enrichment.timeline}</p>
                      </div>
                    )}
                    {displayLead.enrichment.key_terms?.length > 0 && (
                      <div>
                        <label className="text-xs text-theme-secondary uppercase tracking-wider mb-1 block font-mono">
                          KEY TERMS
                        </label>
                        <div className="flex flex-wrap gap-1">
                          {displayLead.enrichment.key_terms.map((term, i) => (
                            <Badge key={i} variant="outline" className="border-theme-primary/40 text-theme-primary-light font-mono text-xs">
                              {term}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-theme-muted">
                    <Sparkles className="w-10 h-10 mx-auto mb-3 text-theme-primary/50" />
                    <p className="font-mono text-sm">CLICK "ENRICH" TO GET AI INSIGHTS</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="email" className="mt-4">
                {displayLead.email_draft ? (
                  <div className="relative">
                    <Textarea
                      value={displayLead.email_draft}
                      readOnly
                      className="bg-theme-dark border-2 border-theme-primary/30 min-h-[200px] text-theme-light font-mono text-sm"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2 text-theme-secondary hover:bg-theme-secondary/20"
                      onClick={() => copyToClipboard(displayLead.email_draft, 'email')}
                      data-testid="copy-email-btn"
                    >
                      {copiedField === 'email' ? (
                        <Check className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8 text-theme-muted">
                    <Mail className="w-10 h-10 mx-auto mb-3 text-theme-primary/50" />
                    <p className="font-mono text-sm">CLICK "GENERATE" TO CREATE EMAIL</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="linkedin" className="mt-4">
                {displayLead.linkedin_draft ? (
                  <div className="relative">
                    <Textarea
                      value={displayLead.linkedin_draft}
                      readOnly
                      className="bg-theme-dark border-2 border-theme-primary/30 min-h-[150px] text-theme-light font-mono text-sm"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2 text-theme-secondary hover:bg-theme-secondary/20"
                      onClick={() => copyToClipboard(displayLead.linkedin_draft, 'linkedin')}
                      data-testid="copy-linkedin-btn"
                    >
                      {copiedField === 'linkedin' ? (
                        <Check className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8 text-theme-muted">
                    <Linkedin className="w-10 h-10 mx-auto mb-3 text-theme-primary/50" />
                    <p className="font-mono text-sm">CLICK "GENERATE" TO CREATE LINKEDIN DM</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="resume" className="mt-4">
                {displayLead.resume_suggestions?.length > 0 || displayLead.questions_to_ask?.length > 0 ? (
                  <div className="space-y-4 p-4 rounded-xl bg-theme-dark/30 border-2 border-theme-primary/20">
                    {displayLead.resume_suggestions?.length > 0 && (
                      <div>
                        <label className="text-xs text-theme-secondary uppercase tracking-wider mb-2 block flex items-center gap-2 font-mono">
                          <FileText className="w-3 h-3" />
                          RESUME BULLETS TO EMPHASIZE
                        </label>
                        <ul className="space-y-2">
                          {displayLead.resume_suggestions.map((suggestion, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-theme-light">
                              <span className="text-theme-primary mt-0.5">•</span>
                              {suggestion}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {displayLead.questions_to_ask?.length > 0 && (
                      <div>
                        <label className="text-xs text-theme-secondary uppercase tracking-wider mb-2 block flex items-center gap-2 font-mono">
                          <HelpCircle className="w-3 h-3" />
                          QUESTIONS TO ASK
                        </label>
                        <ul className="space-y-2">
                          {displayLead.questions_to_ask.map((question, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-theme-light">
                              <span className="text-theme-accent mt-0.5">?</span>
                              {question}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-theme-muted">
                    <FileText className="w-10 h-10 mx-auto mb-3 text-theme-primary/50" />
                    <p className="font-mono text-sm">CLICK "GENERATE" TO GET SUGGESTIONS</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Link & Delete */}
            <div className="flex gap-2 pt-4 border-t-2 border-theme-primary/30">
              {displayLead.link && (
                <Button
                  className="flex-1 bg-theme-dark border-2 border-theme-primary/40 text-theme-primary-light hover:bg-theme-primary/20 font-mono"
                  onClick={() => window.open(displayLead.link, '_blank')}
                  data-testid="open-lead-link-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  OPEN LINK
                </Button>
              )}
              <Button
                className="bg-theme-dark border-2 border-theme-danger/40 text-theme-danger hover:bg-theme-danger/20 font-mono"
                onClick={handleDelete}
                data-testid="delete-lead-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                DELETE
              </Button>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
